import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminService } from '../services/api';
import Navigation from '../components/Navigation';
import { Badge, Button, Card, Input } from '../components/ui';

const CreateAccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [accountType, setAccountType] = useState('client');
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    licenseNumber: '',
    licenseType: '',
    licenseState: '',
    licenseExpiration: '',
    city: '',
    state: '',
    ordinationChurch: '',
    ordinationYear: '',
    planTier: 'basic',
    phone: '',
    assignedClinician: '',
    consentGiven: 'true',
    clientTier: 'foundation'
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');
    if (['admin', 'clinician', 'clergy', 'client'].includes(typeParam)) {
      setAccountType(typeParam);
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setIsLoading(true);

    try {
      const requiredBaseFields = [form.firstName, form.lastName, form.username, form.email, form.password];
      if (requiredBaseFields.some((value) => !value)) {
        setMessage({ type: 'error', text: 'All required fields must be completed.' });
        setIsLoading(false);
        return;
      }

      if (accountType !== 'admin' && form.password.length < 8) {
        setMessage({ type: 'error', text: 'Passwords must be at least 8 characters.' });
        setIsLoading(false);
        return;
      }

      if (accountType === 'clinician') {
        if (!form.licenseNumber || !form.licenseType || !form.licenseState || !form.licenseExpiration) {
          setMessage({ type: 'error', text: 'All clinician fields are required.' });
          setIsLoading(false);
          return;
        }
        await adminService.createClinician({
          firstName: form.firstName,
          lastName: form.lastName,
          username: form.username,
          email: form.email,
          password: form.password,
          licenseNumber: form.licenseNumber,
          licenseType: form.licenseType,
          licenseState: form.licenseState,
          licenseExpiration: form.licenseExpiration,
          city: form.city,
          state: form.state,
          planTier: form.planTier
        });
        setMessage({ type: 'success', text: 'Clinician account created successfully.' });
      } else if (accountType === 'admin') {
        if (form.password.length < 12) {
          setMessage({ type: 'error', text: 'Admin passwords must be at least 12 characters.' });
          setIsLoading(false);
          return;
        }
        await adminService.createAdmin({
          firstName: form.firstName,
          lastName: form.lastName,
          username: form.username,
          email: form.email,
          password: form.password
        });
        setMessage({ type: 'success', text: 'Admin account created successfully.' });
      } else if (accountType === 'clergy') {
        const licenses = [];
        if (form.licenseNumber && form.licenseType && form.licenseState && form.licenseExpiration) {
          licenses.push({
            licenseNumber: form.licenseNumber,
            licenseType: form.licenseType,
            licenseState: form.licenseState,
            licenseExpiration: form.licenseExpiration
          });
        }

        await adminService.createClergy({
          firstName: form.firstName,
          lastName: form.lastName,
          username: form.username,
          email: form.email,
          password: form.password,
          ordinationChurch: form.ordinationChurch,
          ordinationYear: form.ordinationYear ? Number(form.ordinationYear) : null,
          city: form.city,
          state: form.state,
          licenses,
          planTier: 'enterprise'
        });
        setMessage({ type: 'success', text: 'Clergy account created successfully.' });
      } else {
        await adminService.createClient({
          firstName: form.firstName,
          lastName: form.lastName,
          username: form.username,
          email: form.email,
          password: form.password,
          phone: form.phone || undefined,
          assignedClinician: form.assignedClinician || undefined,
          consentGiven: form.consentGiven === 'true',
          clientTier: form.clientTier
        });
        setMessage({ type: 'success', text: 'Client account created successfully.' });
      }

      setForm({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        password: '',
        licenseNumber: '',
        licenseType: '',
        licenseState: '',
        licenseExpiration: '',
        city: '',
        state: '',
        ordinationChurch: '',
        ordinationYear: '',
        planTier: 'basic',
        phone: '',
        assignedClinician: '',
        consentGiven: 'true',
        clientTier: 'foundation'
      });

      setTimeout(() => {
        navigate('/admin');
      }, 2000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || `Failed to create ${accountType} account.`
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin')}
            className="mb-4"
          >
            ← Back to Dashboard
          </Button>
          <Badge variant="primary" size="sm">Account Creation</Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            Create New Account
          </h2>
          <p className="mt-2 text-neutral-600">Provision new users with specialized access levels.</p>
        </div>

        {message && (
          <div
            className={`mb-6 rounded-soft border p-3 text-sm ${
              message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <Card>
          <div className="mb-6">
            <h3 className="text-lg font-display font-semibold text-neutral-800">Account Type</h3>
            <p className="mt-1 text-sm text-neutral-600">Select the type of account to create.</p>
            <div className="mt-4">
              <label className="text-sm text-neutral-700">
                <span className="mb-2 block font-medium">Account Type</span>
                <select
                  name="accountType"
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                  className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <option value="client">Client</option>
                  <option value="clinician">Clinician</option>
                  <option value="clergy">Clergy</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
            </div>
          </div>

          <hr className="my-6 border-neutral-200" />

          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <Input
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              fullWidth
              required
            />
            <Input
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              fullWidth
              required
            />
            <Input
              label="Username"
              name="username"
              value={form.username}
              onChange={handleChange}
              fullWidth
              required
            />
            <Input
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
            />
            <Input
              label={accountType === 'admin' ? 'Password (12+ chars)' : 'Temporary Password'}
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              fullWidth
              required
            />
            {accountType === 'clinician' && (
              <>
                <Input
                  label="License Number"
                  name="licenseNumber"
                  value={form.licenseNumber}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <label className="text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">License Type</span>
                  <select
                    name="licenseType"
                    value={form.licenseType}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    required
                  >
                    <option value="">Select license type</option>
                    <option value="LPC">Licensed Professional Counselor (LPC)</option>
                    <option value="LPC-S">Licensed Professional Counselor-Supervisor (LPC-S)</option>
                    <option value="LCSW">Licensed Clinical Social Worker (LCSW)</option>
                    <option value="LMFT">Licensed Marriage and Family Therapist (LMFT)</option>
                    <option value="LMFT-S">Licensed Marriage and Family Therapist-Supervisor (LMFT-S)</option>
                    <option value="LCDC">Licensed Chemical Dependency Counselor (LCDC)</option>
                    <option value="PsyD">Doctor of Psychology (Psy D)</option>
                    <option value="Psychiatrist">Psychiatrist</option>
                    <option value="Psychologist">Psychologist</option>
                    <option value="MD">Medical Doctor (MD)</option>
                    <option value="PA">Physician Assistant (PA)</option>
                    <option value="NP">Nurse Practitioner (NP)</option>
                    <option value="Life Coach">Life Coach</option>
                  </select>
                </label>
                <label className="text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">License State</span>
                  <select
                    name="licenseState"
                    value={form.licenseState}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    required
                  >
                    <option value="">Select state</option>
                    <option value="AL">Alabama (AL)</option>
                    <option value="AK">Alaska (AK)</option>
                    <option value="AZ">Arizona (AZ)</option>
                    <option value="AR">Arkansas (AR)</option>
                    <option value="CA">California (CA)</option>
                    <option value="CO">Colorado (CO)</option>
                    <option value="CT">Connecticut (CT)</option>
                    <option value="DE">Delaware (DE)</option>
                    <option value="DC">Washington D.C. (DC)</option>
                    <option value="FL">Florida (FL)</option>
                    <option value="GA">Georgia (GA)</option>
                    <option value="HI">Hawaii (HI)</option>
                    <option value="ID">Idaho (ID)</option>
                    <option value="IL">Illinois (IL)</option>
                    <option value="IN">Indiana (IN)</option>
                    <option value="IA">Iowa (IA)</option>
                    <option value="KS">Kansas (KS)</option>
                    <option value="KY">Kentucky (KY)</option>
                    <option value="LA">Louisiana (LA)</option>
                    <option value="ME">Maine (ME)</option>
                    <option value="MD">Maryland (MD)</option>
                    <option value="MA">Massachusetts (MA)</option>
                    <option value="MI">Michigan (MI)</option>
                    <option value="MN">Minnesota (MN)</option>
                    <option value="MS">Mississippi (MS)</option>
                    <option value="MO">Missouri (MO)</option>
                    <option value="MT">Montana (MT)</option>
                    <option value="NE">Nebraska (NE)</option>
                    <option value="NV">Nevada (NV)</option>
                    <option value="NH">New Hampshire (NH)</option>
                    <option value="NJ">New Jersey (NJ)</option>
                    <option value="NM">New Mexico (NM)</option>
                    <option value="NY">New York (NY)</option>
                    <option value="NC">North Carolina (NC)</option>
                    <option value="ND">North Dakota (ND)</option>
                    <option value="OH">Ohio (OH)</option>
                    <option value="OK">Oklahoma (OK)</option>
                    <option value="OR">Oregon (OR)</option>
                    <option value="PA">Pennsylvania (PA)</option>
                    <option value="RI">Rhode Island (RI)</option>
                    <option value="SC">South Carolina (SC)</option>
                    <option value="SD">South Dakota (SD)</option>
                    <option value="TN">Tennessee (TN)</option>
                    <option value="TX">Texas (TX)</option>
                    <option value="UT">Utah (UT)</option>
                    <option value="VT">Vermont (VT)</option>
                    <option value="VA">Virginia (VA)</option>
                    <option value="WA">Washington (WA)</option>
                    <option value="WV">West Virginia (WV)</option>
                    <option value="WI">Wisconsin (WI)</option>
                    <option value="WY">Wyoming (WY)</option>
                  </select>
                </label>
                <Input
                  label="License Expiration"
                  name="licenseExpiration"
                  type="date"
                  value={form.licenseExpiration}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <Input
                  label="City"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  fullWidth
                />
                <Input
                  label="State (2-letter code)"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  fullWidth
                />
                <label className="text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">Plan Tier</span>
                  <select
                    name="planTier"
                    value={form.planTier}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    required
                  >
                    <option value="basic">Basic (5 clients)</option>
                    <option value="pro">Pro (25 clients)</option>
                    <option value="enterprise">Enterprise (Unlimited)</option>
                  </select>
                </label>
              </>
            )}

            {accountType === 'clergy' && (
              <>
                <Input
                  label="Church of Ordination"
                  name="ordinationChurch"
                  value={form.ordinationChurch}
                  onChange={handleChange}
                  fullWidth
                />
                <Input
                  label="Year Ordained"
                  name="ordinationYear"
                  type="number"
                  value={form.ordinationYear}
                  onChange={handleChange}
                  fullWidth
                />
                <Input
                  label="City"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  fullWidth
                />
                <Input
                  label="State (2-letter code)"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  fullWidth
                />
                <Input
                  label="Professional License Number (optional)"
                  name="licenseNumber"
                  value={form.licenseNumber}
                  onChange={handleChange}
                  fullWidth
                />
                <label className="text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">Professional License Type (optional)</span>
                  <select
                    name="licenseType"
                    value={form.licenseType}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="">Select license type</option>
                    <option value="LPC">Licensed Professional Counselor (LPC)</option>
                    <option value="LPC-S">Licensed Professional Counselor-Supervisor (LPC-S)</option>
                    <option value="LCSW">Licensed Clinical Social Worker (LCSW)</option>
                    <option value="LMFT">Licensed Marriage and Family Therapist (LMFT)</option>
                    <option value="LMFT-S">Licensed Marriage and Family Therapist-Supervisor (LMFT-S)</option>
                    <option value="LCDC">Licensed Chemical Dependency Counselor (LCDC)</option>
                    <option value="PsyD">Doctor of Psychology (Psy D)</option>
                    <option value="Psychiatrist">Psychiatrist</option>
                    <option value="Psychologist">Psychologist</option>
                    <option value="MD">Medical Doctor (MD)</option>
                    <option value="PA">Physician Assistant (PA)</option>
                    <option value="NP">Nurse Practitioner (NP)</option>
                    <option value="Life Coach">Life Coach</option>
                  </select>
                </label>
                <label className="text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">Professional License State (optional)</span>
                  <select
                    name="licenseState"
                    value={form.licenseState}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="">Select state</option>
                    <option value="AL">Alabama (AL)</option>
                    <option value="AK">Alaska (AK)</option>
                    <option value="AZ">Arizona (AZ)</option>
                    <option value="AR">Arkansas (AR)</option>
                    <option value="CA">California (CA)</option>
                    <option value="CO">Colorado (CO)</option>
                    <option value="CT">Connecticut (CT)</option>
                    <option value="DE">Delaware (DE)</option>
                    <option value="DC">Washington D.C. (DC)</option>
                    <option value="FL">Florida (FL)</option>
                    <option value="GA">Georgia (GA)</option>
                    <option value="HI">Hawaii (HI)</option>
                    <option value="ID">Idaho (ID)</option>
                    <option value="IL">Illinois (IL)</option>
                    <option value="IN">Indiana (IN)</option>
                    <option value="IA">Iowa (IA)</option>
                    <option value="KS">Kansas (KS)</option>
                    <option value="KY">Kentucky (KY)</option>
                    <option value="LA">Louisiana (LA)</option>
                    <option value="ME">Maine (ME)</option>
                    <option value="MD">Maryland (MD)</option>
                    <option value="MA">Massachusetts (MA)</option>
                    <option value="MI">Michigan (MI)</option>
                    <option value="MN">Minnesota (MN)</option>
                    <option value="MS">Mississippi (MS)</option>
                    <option value="MO">Missouri (MO)</option>
                    <option value="MT">Montana (MT)</option>
                    <option value="NE">Nebraska (NE)</option>
                    <option value="NV">Nevada (NV)</option>
                    <option value="NH">New Hampshire (NH)</option>
                    <option value="NJ">New Jersey (NJ)</option>
                    <option value="NM">New Mexico (NM)</option>
                    <option value="NY">New York (NY)</option>
                    <option value="NC">North Carolina (NC)</option>
                    <option value="ND">North Dakota (ND)</option>
                    <option value="OH">Ohio (OH)</option>
                    <option value="OK">Oklahoma (OK)</option>
                    <option value="OR">Oregon (OR)</option>
                    <option value="PA">Pennsylvania (PA)</option>
                    <option value="RI">Rhode Island (RI)</option>
                    <option value="SC">South Carolina (SC)</option>
                    <option value="SD">South Dakota (SD)</option>
                    <option value="TN">Tennessee (TN)</option>
                    <option value="TX">Texas (TX)</option>
                    <option value="UT">Utah (UT)</option>
                    <option value="VT">Vermont (VT)</option>
                    <option value="VA">Virginia (VA)</option>
                    <option value="WA">Washington (WA)</option>
                    <option value="WV">West Virginia (WV)</option>
                    <option value="WI">Wisconsin (WI)</option>
                    <option value="WY">Wyoming (WY)</option>
                  </select>
                </label>
                <Input
                  label="Professional License Expiration (optional)"
                  name="licenseExpiration"
                  type="date"
                  value={form.licenseExpiration}
                  onChange={handleChange}
                  fullWidth
                />
              </>
            )}

            {accountType === 'client' && (
              <>
                <label className="text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">Client Tier</span>
                  <select
                    name="clientTier"
                    value={form.clientTier}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="foundation">Foundation</option>
                    <option value="guided">Guided</option>
                  </select>
                </label>
                <Input
                  label="Phone (optional)"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  fullWidth
                />
                <Input
                  label="Assigned Clinician ID (optional)"
                  name="assignedClinician"
                  value={form.assignedClinician}
                  onChange={handleChange}
                  fullWidth
                />
                <label className="text-sm text-neutral-700">
                  <span className="mb-2 block font-medium">Consent Given</span>
                  <select
                    name="consentGiven"
                    value={form.consentGiven}
                    onChange={handleChange}
                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
              </>
            )}
            <div className="flex gap-3">
              <Button type="submit" className="flex-1" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Account'}
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </>
  );
};

export default CreateAccountPage;
